import * as React from 'react'
import { RefreshCwIcon, Button } from '@buffer-mono/popcorn'
import { ActionButton } from '~publish/legacy/ai/components/AIAssistant/components/ActionButton'
import { CopyButton } from '~publish/legacy/ai/components/AIAssistant/components/CopyButton'
import { Error } from '~publish/legacy/ai/components/AIAssistant/components/Error'
import { FunFact } from '~publish/legacy/ai/components/AIAssistant/components/FunFact'
import { Loader } from '~publish/legacy/ai/components/AIAssistant/components/Loader'
import { RetryButton } from '~publish/legacy/ai/components/AIAssistant/components/RetryButton'
import { ToneButton } from '~publish/legacy/ai/components/AIAssistant/components/ToneButton'
import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'
import { AIAssistantContext } from '../AIAssistant'

import {
  resetContentGeneration,
  resetSelection,
  selectContentGenerationStatus,
} from '~publish/legacy/ai/state/slice'

import { useQueryParam } from '../../../../../hooks/useQueryParam'
import styles from './GenerationResult.module.css'

/**
 *
 */
function GenerationResult({ disabled }: { disabled?: boolean }): JSX.Element {
  const dispatch = useAppDispatch()
  const [, setAiAssisted] = useQueryParam('aiAssisted')

  const { editor } = React.useContext(AIAssistantContext)

  const {
    contentGenerationInProgress,
    contentGenerationError,
    contentGenerationResults,
  } = useAppSelector((state) => selectContentGenerationStatus(state))

  const onAddContent = (): void => {
    editor?.addGeneratedContent?.({ content })
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0
    dispatch(resetContentGeneration())
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0
    dispatch(resetSelection())

    // For Composer: handles setting all drafts as AI assisted.
    ComposerActionCreators.updateAiAssist()

    // For Ideas Editor: handles setting current idea as AI assisted.
    // When content is inserted/replaced, we add 'aiAssisted' query param to the URL
    // with 'replace' to ensure back navigation still operates.
    setAiAssisted('true', { replace: true })
  }

  const content = contentGenerationResults[0]

  const showLoader = contentGenerationInProgress
  const showError = contentGenerationError
  const showContent = content && !showLoader && !showError

  const shouldReplace = editor && editor?.getSelection?.()

  return (
    <>
      <div className={styles.verticalWrapper}>
        {contentGenerationError && <Error />}

        {showContent && (
          <div className={styles.wrapper}>
            <div
              className={`${styles.contentWrapper} ${
                disabled ? styles.disabled : ''
              }`}
            >
              <pre
                className={`${styles.content} ${
                  disabled ? styles.disabled : ''
                }`}
                data-testid="ai-assistant-content-generated"
              >
                {content.trim()}
              </pre>
              <div className={styles.buttonWrapper}>
                <CopyButton disabled={disabled} />
                <div className={styles.primaryButtonsWrapper}>
                  <RetryButton disabled={disabled}>
                    <RefreshCwIcon />
                    Retry
                  </RetryButton>
                  <Button
                    onClick={onAddContent}
                    disabled={disabled}
                    className={styles.insertButton}
                  >
                    {shouldReplace ? 'Replace' : 'Insert'}
                  </Button>
                </div>
              </div>
            </div>
            <div
              className={styles.actionsWrapper}
              id="ai-assistant-fine-tuning-container"
            >
              {['rephrase', 'shorten', 'expand'].map((action, index) => (
                <ActionButton
                  target={content}
                  action={action}
                  disabled={disabled}
                  key={index}
                  testId={`ai-assistant-fine-tuning-${action}-button`}
                  secondary
                />
              ))}
              {['casual', 'formal'].map((tone, index) => (
                <ToneButton
                  tone={tone}
                  disabled={disabled}
                  key={index}
                  testId={`ai-assistant-fine-tuning-${tone}-button`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {contentGenerationInProgress && (
        <div className={styles.loadingWrapper}>
          <Loader />
          <FunFact />
        </div>
      )}
    </>
  )
}

export { GenerationResult }
